import { useMemo } from 'react'
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DailyDashboardStatDto } from '@api/types/dashboard'
import { CHART_COLORS } from './chartConfig'

interface CompletionsChartProps {
  data?: DailyDashboardStatDto[]
  isLoading?: boolean
}

export function CompletionsChart({ data, isLoading }: CompletionsChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const chartData = useMemo(() => {
    if (!data) return []
    return data.map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      soloCompletions: item.soloCompletions,
      groupCompletions: item.groupCompletions,
    }))
  }, [data])

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Completions
      </Typography>
      {isLoading ? (
        <Box sx={{ height: 300 }}>
          <Skeleton variant="rectangular" height="100%" />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fill: axisColor, fontSize: 12 }} />
            <YAxis tick={{ fill: axisColor, fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="soloCompletions"
              name="Solo"
              stackId="1"
              stroke={CHART_COLORS.soloCompletions}
              fill={CHART_COLORS.soloCompletions}
              fillOpacity={0.6}
            />
            <Area
              type="monotone"
              dataKey="groupCompletions"
              name="Group"
              stackId="1"
              stroke={CHART_COLORS.groupCompletions}
              fill={CHART_COLORS.groupCompletions}
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  )
}
